import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

/* ─── Stats Bar ───────────────────────────────────────────────────────────── */
function StatsBar({ stats, accent }) {
  return (
    <section className={`bg-gradient-to-r ${accent || "from-blue-600 to-indigo-700"} py-10`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          {stats.map((s, i) => (
            <motion.div key={i} {...fadeUp(i * 0.07)}>
              <div className="text-3xl lg:text-4xl font-extrabold mb-1">{s.value}</div>
              <div className="text-xs lg:text-sm font-medium uppercase tracking-widest text-white/75">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Challenges Grid ─────────────────────────────────────────────────────── */
function ChallengesSection({ title, subtitle, challenges, theme }) {
  const bg = theme?.challengeBg || "bg-slate-900";
  const titleColor = theme?.challengeTitle || "text-white";
  const subtitleColor = theme?.challengeSubtitle || "text-slate-400";
  const cardBg = theme?.challengeCard || "bg-slate-800/60";
  const cardBorder = theme?.challengeBorder || "border-slate-700";
  const iconBg = theme?.challengeIconBg || "bg-slate-700";
  const iconColor = theme?.challengeIconColor || "text-white";

  return (
    <section className={`${bg} py-20`}>
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full bg-white/10 text-white/80 border border-white/20 mb-4">
            The Challenge
          </span>
          <h2 className={`text-3xl lg:text-4xl font-extrabold ${titleColor} mb-4`}>
            {title}
          </h2>
          {subtitle && (
            <p className={`text-lg ${subtitleColor} max-w-2xl mx-auto`}>{subtitle}</p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {challenges.map((c, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.06)}
              className={`${cardBg} border ${cardBorder} rounded-2xl p-6 hover:border-white/30 transition-all duration-300`}
            >
              <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center text-xl mb-4`}>
                <span>{c.icon}</span>
              </div>
              <h3 className={`font-bold text-base ${titleColor} mb-2`}>{c.title}</h3>
              <p className={`text-sm leading-relaxed ${subtitleColor}`}>{c.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Solutions / Features Grid ───────────────────────────────────────────── */
function SolutionsSection({ title, subtitle, solutions, theme }) {
  const accent = theme?.solutionAccent || "from-blue-600 to-indigo-600";
  const badgeBg = theme?.badgeBg || "bg-blue-50 text-blue-700 border-blue-100";
  const iconBg = theme?.solutionIconBg || "bg-blue-50";
  const iconColor = theme?.solutionIconColor || "text-blue-600";

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="text-center mb-12">
          <span className={`inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full border ${badgeBg} mb-4`}>
            Our Solution
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-4">{title}</h2>
          {subtitle && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.06)}
              className="group bg-slate-50 border border-slate-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <span>{s.icon}</span>
              </div>
              <h3 className="font-bold text-slate-900 text-base mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Outcomes Section ────────────────────────────────────────────────────── */
function OutcomesSection({ title, subtitle, outcomes, accent, image }) {
  const grad = accent || "from-blue-600 to-indigo-700";

  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 mb-4">
            The Results
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-4">{title}</h2>
          {subtitle && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {outcomes.map((o, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.1)}
              className={`bg-gradient-to-br ${grad} rounded-2xl p-8 text-white text-center shadow-lg`}
            >
              <div className="text-4xl lg:text-5xl font-extrabold mb-2">{o.value}</div>
              <div className="font-bold text-lg mb-2 text-white/90">{o.metric}</div>
              <p className="text-sm text-white/70 leading-relaxed">{o.description}</p>
            </motion.div>
          ))}
        </div>

        {image && (
          <motion.div {...fadeUp(0.2)} className="rounded-2xl overflow-hidden shadow-xl max-w-4xl mx-auto">
            <img src={image} alt="Outcome" className="w-full h-64 lg:h-80 object-cover" />
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ─── How It Works ────────────────────────────────────────────────────────── */
function HowItWorksSection({ steps, title, subtitle, theme }) {
  if (!steps || steps.length === 0) return null;
  const accent = theme?.solutionAccent || "from-blue-600 to-indigo-600";

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div {...fadeUp()} className="text-center mb-14">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 mb-4">
            How It Works
          </span>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-4">
            {title || "From Setup to Results in Days"}
          </h2>
          {subtitle && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-slate-200 via-blue-200 to-slate-200" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div key={i} {...fadeUp(i * 0.1)} className="relative flex flex-col items-center text-center">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center text-3xl text-white shadow-lg mb-5 relative z-10`}>
                  {step.icon}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center z-20">
                  {i + 1}
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── CTA Section ─────────────────────────────────────────────────────────── */
function CTASection({ title, subtitle, ctaLabel, ctaLink, accent }) {
  const grad = accent || "from-blue-600 to-indigo-700";

  return (
    <section className={`bg-gradient-to-br ${grad} py-20`}>
      <div className="container mx-auto px-4 text-center">
        <motion.div {...fadeUp()}>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4">{title}</h2>
          {subtitle && (
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">{subtitle}</p>
          )}
          <a
            href={ctaLink || "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled"}
            target="_blank"
            rel="noreferrer"
            className="inline-block bg-white text-blue-700 font-extrabold text-base px-8 py-4 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          >
            {ctaLabel || "Book a Free Consultation"}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Main Export ─────────────────────────────────────────────────────────── */
const EnhancedPageLayout = ({
  stats,
  statsAccent,
  challengeTitle,
  challengeSubtitle,
  challenges,
  solutionTitle,
  solutionSubtitle,
  solutions,
  howItWorksTitle,
  howItWorksSubtitle,
  howItWorksSteps,
  outcomeTitle,
  outcomeSubtitle,
  outcomes,
  outcomeImage,
  ctaTitle,
  ctaSubtitle,
  ctaLabel,
  ctaLink,
  theme,
}) => {
  return (
    <>
      {stats?.length > 0 && (
        <StatsBar stats={stats} accent={statsAccent} />
      )}
      {challenges?.length > 0 && (
        <ChallengesSection
          title={challengeTitle}
          subtitle={challengeSubtitle}
          challenges={challenges}
          theme={theme}
        />
      )}
      {solutions?.length > 0 && (
        <SolutionsSection
          title={solutionTitle}
          subtitle={solutionSubtitle}
          solutions={solutions}
          theme={theme}
        />
      )}
      {howItWorksSteps?.length > 0 && (
        <HowItWorksSection
          steps={howItWorksSteps}
          title={howItWorksTitle}
          subtitle={howItWorksSubtitle}
          theme={theme}
        />
      )}
      {outcomes?.length > 0 && (
        <OutcomesSection
          title={outcomeTitle}
          subtitle={outcomeSubtitle}
          outcomes={outcomes}
          accent={statsAccent}
          image={outcomeImage}
        />
      )}
      <CTASection
        title={ctaTitle}
        subtitle={ctaSubtitle}
        ctaLabel={ctaLabel}
        ctaLink={ctaLink}
        accent={statsAccent}
      />
    </>
  );
};

export default EnhancedPageLayout;
